import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { User } from 'lucide-react'
import { cn } from '@/lib/utils'

interface LoginDropdownProps {
  isTransparent: boolean
  variant?: 'desktop' | 'mobile'
}

export function LoginDropdown({ isTransparent, variant = 'desktop' }: LoginDropdownProps) {
  const { t } = useTranslation('common')
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (variant !== 'desktop') return

    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false)
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, variant])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
  }

  const form = (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h3 className="font-heading font-semibold text-neutral-900 text-lg">
        {t('login.title')}
      </h3>
      <div>
        <label htmlFor={`login-email-${variant}`} className="block text-sm font-medium text-neutral-700 mb-1">
          {t('login.email')}
        </label>
        <input
          id={`login-email-${variant}`}
          type="email"
          className="w-full px-4 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-brand focus:border-brand outline-none"
        />
      </div>
      <div>
        <label htmlFor={`login-password-${variant}`} className="block text-sm font-medium text-neutral-700 mb-1">
          {t('login.password')}
        </label>
        <input
          id={`login-password-${variant}`}
          type="password"
          className="w-full px-4 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-brand focus:border-brand outline-none"
        />
      </div>
      <button
        type="submit"
        className="w-full bg-brand text-neutral-900 font-heading font-medium py-2 rounded-md hover:bg-brand-dark transition-colors"
      >
        {t('login.submit')}
      </button>
    </form>
  )

  if (variant === 'mobile') {
    return (
      <div className="pt-4 border-t border-neutral-200">
        {form}
      </div>
    )
  }

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'p-2 rounded-md transition-colors',
          isTransparent
            ? 'text-white hover:bg-white/10'
            : 'text-neutral-700 hover:bg-neutral-100'
        )}
        aria-label={t('aria.openLogin')}
        aria-expanded={isOpen}
      >
        <User className="w-5 h-5" />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-80 p-6 bg-white rounded-lg shadow-xl border border-neutral-200 z-dropdown">
          {form}
        </div>
      )}
    </div>
  )
}
